/**
 * Avisos (toasts) y confirmaciones sin depender de React en el punto de
 * llamada: cualquier módulo (helpers de subida de archivos, mutaciones,
 * páginas) llama a `toast.success('Guardado')` o `await confirmar({...})` y
 * el `ToastProvider` (ui/Toast.tsx) los pinta. Sustituye a los `alert()` y
 * `confirm()` del navegador (rediseño UX fase 1, 16/09/2026).
 */

export type ToastKind = 'success' | 'error' | 'info' | 'warning';

export type ToastItem = {
  id: number;
  kind: ToastKind;
  /** Primera línea, corta. */
  title: string;
  /** Detalle opcional (varias líneas admitidas). */
  detail?: string;
  /** ms; los errores duran más por defecto. */
  duration: number;
};

export type ConfirmOptions = {
  title: string;
  message?: string;
  /** Texto del botón que confirma. Default "Confirmar". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Pinta el botón de confirmar en rojo (acciones destructivas). */
  danger?: boolean;
};

type Listener = {
  onToast: (t: ToastItem) => void;
  onConfirm: (o: ConfirmOptions, resolve: (ok: boolean) => void) => void;
};

let listener: Listener | null = null;
let seq = 0;
/** Cola de lo emitido antes de montar el provider (raro, pero no se pierde). */
const pendientes: ToastItem[] = [];

export function registrarListener(l: Listener | null): void {
  listener = l;
  if (l) { for (const t of pendientes.splice(0)) l.onToast(t); }
}

function emitir(kind: ToastKind, title: string, detail?: string, duration?: number): void {
  const t: ToastItem = { id: ++seq, kind, title, detail, duration: duration ?? (kind === 'error' ? 8000 : 4000) };
  if (listener) listener.onToast(t);
  else pendientes.push(t);
}

export const toast = {
  success: (title: string, detail?: string) => emitir('success', title, detail),
  error: (title: string, detail?: string) => emitir('error', title, detail),
  info: (title: string, detail?: string) => emitir('info', title, detail),
  warning: (title: string, detail?: string) => emitir('warning', title, detail),
};

/**
 * Confirmación modal. Resuelve true si el usuario confirma. Si el provider no
 * está montado (tests), cae al confirm() nativo para no bloquear.
 */
export function confirmar(opts: ConfirmOptions): Promise<boolean> {
  if (!listener) return Promise.resolve(typeof window !== 'undefined' ? window.confirm(`${opts.title}${opts.message ? `\n\n${opts.message}` : ''}`) : false);
  return new Promise((resolve) => listener!.onConfirm(opts, resolve));
}
